import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { BusyButton, ConfirmDialog, CopyButton, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { EmptyPlugMark, EmptyState, ErrorState, Skeleton } from "../../components/States";
import { OrgLayout } from "../../components/Shell";
import { useCan } from "../../auth/roles";
import { DESK_SETUP, DESK_TYPES, desksApi, type DeskInput, type DeskProvider, type SupportDesk } from "./api";

export type DeskDraft = { step: "type" | "config"; provider: DeskProvider; name: string; config: Record<string, string>; secrets: Record<string, string> };

const emptyWizard = (): DeskDraft => ({ step: "type", provider: "zendesk", name: "", config: {}, secrets: {} });

function kept(value: string | undefined): boolean {
  return !!value?.trim();
}

/** Required fields for a new desk. The server checks the same rules. */
export function validateDesk(w: DeskDraft): Record<string, string> {
  const e: Record<string, string> = {};
  if (!w.name.trim()) e.name = "Name the desk.";
  const site = ((w.provider === "servicenow" ? w.config.instance : w.config.site) ?? "").trim();
  const siteOk = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(site);
  if (w.provider === "zendesk" || w.provider === "freshdesk") {
    if (!siteOk) e.site = "Enter the site name, such as acme.";
  }
  if (w.provider === "zendesk" && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(w.config.email ?? "")) e.email = "Enter the agent email.";
  if (w.provider === "zendesk" && !kept(w.secrets.apiToken)) e.apiToken = "Enter the API token.";
  if (w.provider === "freshdesk" && !kept(w.secrets.apiKey)) e.apiKey = "Enter the API key.";
  if (w.provider === "intercom" && !kept(w.secrets.accessToken)) e.accessToken = "Enter the access token.";
  if (w.provider === "salesforce") {
    let host = "";
    try {
      const u = new URL(w.config.instanceUrl ?? "");
      host = u.hostname.toLowerCase();
      if (u.protocol !== "https:") host = "";
    } catch {
      host = "";
    }
    if (!host.endsWith(".my.salesforce.com") && !host.endsWith(".salesforce.com")) e.instanceUrl = "Enter the Salesforce site address, starting with https.";
    else if (host === "salesforce.com" || host === "my.salesforce.com") e.instanceUrl = "Enter your Salesforce site, not the generic address.";
    if (!kept(w.secrets.accessToken)) e.accessToken = "Enter the access token.";
  }
  if (w.provider === "servicenow") {
    if (!siteOk) e.instance = "Enter the instance name, such as acme.";
    if (!kept(w.config.username)) e.username = "Enter the user name.";
    if (!kept(w.secrets.password)) e.password = "Enter the password.";
  }
  return e;
}

export function toDeskInput(w: DeskDraft): DeskInput {
  return {
    provider: w.provider,
    name: w.name.trim(),
    config: Object.fromEntries(Object.entries(w.config).map(([k, v]) => [k, v.trim()])),
    secrets: Object.fromEntries(Object.entries(w.secrets).filter(([, v]) => v.trim())),
  };
}

export function DesksPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const desks = useQuery({ queryKey: ["support-desks", orgId], queryFn: () => desksApi.list(orgId) });
  const [wizard, setWizard] = useState<DeskDraft | null>(null);
  const [setup, setSetup] = useState<SupportDesk | null>(null);
  const [removing, setRemoving] = useState<SupportDesk | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["support-desks", orgId] });

  const save = useMutation({
    mutationFn: (w: DeskDraft) => desksApi.create(orgId, toDeskInput(w)),
    onSuccess: (d) => {
      setWizard(null);
      setSetup(d);
      void invalidate();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const toggle = useMutation({
    mutationFn: (d: SupportDesk) => desksApi.patch(orgId, d.id, { enabled: !d.enabled }),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => desksApi.remove(orgId, id),
    onSuccess: () => {
      setRemoving(null);
      toast.push("info", "Desk removed.");
      void invalidate();
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const label = (p: string) => DESK_TYPES.find((t) => t.id === p)?.label ?? p;

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader
        title="Support desks"
        subtitle="When a ticket is created, the customer's latest session is added to it. If they are still on the page, the note includes Live Assist."
        actions={can.admin ? <button className="btn primary" onClick={() => setWizard(emptyWizard())}>Add desk</button> : undefined}
      />
      {desks.isPending ? <Skeleton lines={4} /> : null}
      {desks.isError ? <ErrorState error={desks.error} retry={() => void desks.refetch()} /> : null}
      {desks.data && desks.data.length === 0 ? (
        <EmptyState mark={<EmptyPlugMark />} title="No desks connected" hint="Connect Zendesk, Intercom, Salesforce, Freshdesk, or ServiceNow." action={can.admin ? <button className="btn primary" onClick={() => setWizard(emptyWizard())}>Add desk</button> : undefined} />
      ) : null}
      {desks.data && desks.data.length > 0 ? (
        <div className="stack-table">
        <DataTable
          rows={desks.data}
          rowKey={(d) => d.id}
          ariaLabel="Support desks"
          columns={[
            { key: "name", header: "Name", label: "Name", render: (d) => (<><strong>{d.name}</strong>{d.lastError ? <div className="danger small">{d.lastError}</div> : null}</>) },
            { key: "desk", header: "Desk", label: "Desk", render: (d) => label(d.provider) },
            { key: "on", header: "On", label: "On", render: (d) => <Switch checked={d.enabled} onChange={() => toggle.mutate(d)} label={d.enabled ? "On" : "Off"} /> },
            {
              key: "menu", header: "", label: "Actions", render: (d) => (
                <RowMenu label={`Actions for ${d.name}`}>
                  <button type="button" role="menuitem" onClick={() => setSetup(d)}>Setup</button>
                  <button type="button" className="danger" role="menuitem" onClick={() => setRemoving(d)}>Remove desk</button>
                </RowMenu>
              ),
            },
          ]}
        />
        </div>
      ) : null}
      {wizard ? <DeskWizard wizard={wizard} taken={new Set((desks.data ?? []).map((d) => d.provider))} saving={save.isPending} onChange={setWizard} onClose={() => setWizard(null)} onSave={() => save.mutate(wizard)} /> : null}
      <SetupDialog desk={setup} onClose={() => setSetup(null)} />
      <ConfirmDialog open={!!removing} danger title="Remove desk" confirmLabel="Remove desk" busy={del.isPending} onClose={() => setRemoving(null)} onConfirm={() => removing && del.mutate(removing.id)}>
        <p style={{ margin: 0 }}>New tickets in <strong>{removing?.name}</strong> will no longer get a session.</p>
      </ConfirmDialog>
    </OrgLayout>
  );
}

function SetupDialog({ desk, onClose }: { desk: SupportDesk | null; onClose: () => void }) {
  if (!desk) return null;
  const setup = DESK_SETUP[desk.provider];
  return (
    <Dialog open onClose={onClose} title={`Connect ${DESK_TYPES.find((t) => t.id === desk.provider)?.label ?? "desk"}`} footer={<button className="btn primary" onClick={onClose}>Done</button>}>
      <p>{setup.where}</p>
      {desk.webhookUrl ? (
        <div className="secret-box">
          <code className="mono">{desk.webhookUrl}</code>
          <CopyButton value={desk.webhookUrl} />
        </div>
      ) : null}
      {setup.body ? (
        <>
          <p>Send this body.</p>
          <div className="secret-box">
            <code className="mono">{setup.body}</code>
            <CopyButton value={setup.body} />
          </div>
        </>
      ) : null}
    </Dialog>
  );
}

function DeskWizard({ wizard, taken, saving, onChange, onClose, onSave }: {
  wizard: DeskDraft;
  taken: Set<string>;
  saving: boolean;
  onChange: (w: DeskDraft) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const meta = DESK_TYPES.find((t) => t.id === wizard.provider)!;
  const problems = validateDesk(wizard);
  const ready = wizard.step === "type" || Object.keys(problems).length === 0;
  const setCfg = (k: string, v: string) => onChange({ ...wizard, config: { ...wizard.config, [k]: v } });
  const setSecret = (k: string, v: string) => onChange({ ...wizard, secrets: { ...wizard.secrets, [k]: v } });
  const text = (k: string, label: string, opts: { secret?: boolean; placeholder?: string; hint?: string } = {}) => (
    <Field key={k} label={label} hint={opts.hint} error={problems[k]}>
      {(p) => (
        <input {...p} className="input" type={opts.secret ? "password" : "text"} autoComplete="off" placeholder={opts.placeholder} value={(opts.secret ? wizard.secrets[k] : wizard.config[k]) ?? ""} onChange={(e) => (opts.secret ? setSecret(k, e.target.value) : setCfg(k, e.target.value))} />
      )}
    </Field>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title={wizard.step === "type" ? "Add desk" : `Add ${meta.label}`}
      width={640}
      footer={wizard.step === "type" ? (
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={() => onChange({ ...wizard, step: "config", name: wizard.name || meta.label })}>Continue</button>
        </>
      ) : (
        <>
          <button className="btn" onClick={() => onChange({ ...wizard, step: "type" })} disabled={saving}>Back</button>
          <BusyButton className="btn primary" onClick={onSave} busy={saving} busyLabel="Saving…" disabled={!ready}>{`Connect ${meta.label}`}</BusyButton>
        </>
      )}
    >
      {wizard.step === "type" ? (
        <div className="type-grid" role="radiogroup" aria-label="Desk">
          {DESK_TYPES.map((t) => {
            const used = taken.has(t.id);
            return (
              <label key={t.id} className={`type-card ${wizard.provider === t.id ? "selected" : ""} ${used ? "disabled" : ""}`}>
                <input type="radio" name="desk-type" value={t.id} checked={wizard.provider === t.id} disabled={used} onChange={() => onChange({ ...wizard, provider: t.id, config: {}, secrets: {} })} />
                <strong>{t.label}</strong>
                <span className="muted small">{used ? "Already connected" : t.description}</span>
              </label>
            );
          })}
        </div>
      ) : (
        <div className="form-grid">
          <Field label="Name" error={problems.name}>{(p) => <input {...p} className="input" value={wizard.name} onChange={(e) => onChange({ ...wizard, name: e.target.value })} autoFocus />}</Field>
          {(wizard.provider === "zendesk" || wizard.provider === "freshdesk") && text("site", "Site", { placeholder: "acme", hint: wizard.provider === "zendesk" ? "The name in acme.zendesk.com." : "The name in acme.freshdesk.com." })}
          {wizard.provider === "servicenow" && text("instance", "Instance", { placeholder: "acme", hint: "The name in acme.service-now.com." })}
          {wizard.provider === "servicenow" && text("username", "User name")}
          {wizard.provider === "servicenow" && text("password", "Password", { secret: true, hint: "Stored encrypted. This user must be allowed to write work notes." })}
          {wizard.provider === "zendesk" && text("email", "Agent email")}
          {wizard.provider === "zendesk" && text("apiToken", "API token", { secret: true, hint: "Stored encrypted." })}
          {wizard.provider === "freshdesk" && text("apiKey", "API key", { secret: true, hint: "Stored encrypted." })}
          {wizard.provider === "intercom" && text("accessToken", "Access token", { secret: true, hint: "Stored encrypted." })}
          {wizard.provider === "salesforce" && text("instanceUrl", "Site address", { placeholder: "https://acme.my.salesforce.com" })}
          {wizard.provider === "salesforce" && text("accessToken", "Access token", { secret: true, hint: "Stored encrypted." })}
        </div>
      )}
    </Dialog>
  );
}
